import { AnnotationType, GeometryData } from '../../api/protosCompiled/annotation/annotation_pb';
import { useCallback } from 'react';
import { EnhancedAnnotation, ProtoAnnotationType } from './types';
import { useUpdateShapeAnnotationMutation } from './useAnnotationHooks';
import { useAnnotationStore } from './annotationStore';
import { constants } from './constatnt';

// Get default style based on annotation type - include both camelCase and lowercase keys
export const useGetDefaultStyle = () => {
  const getDefaultStyle = useCallback((type: AnnotationType) => {
    const baseCamel = {
      fontSize: constants.text.fontSize,
      fontFamily: constants.text.fontFamily,
      fontWeight: constants.text.fontWeight,
      textAnchor: constants.text.textAnchor,
    };
    const baseLower = {
      fontsize: constants.text.fontSize,
      fontfamily: constants.text.fontFamily,
      fontweight: constants.text.fontWeight,
      textanchor: constants.text.textAnchor,
    };

    switch (type) {
      case AnnotationType.TEXT: {
        const cam = {
          fill: constants.text.fill,
          stroke: constants.text.stroke,
          strokeWidth: constants.text.strokeWidth,
          strokeDasharray: constants.text.strokeDasharray,
          opacity: constants.text.opacity,
          ...baseCamel,
        };
        const low = {
          fill: cam.fill,
          stroke: cam.stroke,
          strokewidth: cam.strokeWidth,
          strokedasharray: cam.strokeDasharray,
          opacity: cam.opacity,
          ...baseLower,
        };
        return { ...cam, ...low };
      }
      case AnnotationType.RECTANGLE: {
        const cam = {
          fill: constants.shape.fill,
          stroke: constants.shape.stroke,
          strokeWidth: constants.shape.strokeWidth,
          strokeDasharray: constants.shape.strokeDasharray,
          opacity: constants.shape.opacity,
          ...baseCamel,
        };
        const low = {
          fill: cam.fill,
          stroke: cam.stroke,
          strokewidth: cam.strokeWidth,
          strokedasharray: cam.strokeDasharray,
          opacity: cam.opacity,
          ...baseLower,
        };
        return { ...cam, ...low };
      }
      case AnnotationType.CIRCLE: {
        const cam = {
          fill: constants.shape.fill,
          stroke: constants.shape.stroke,
          strokeWidth: constants.shape.strokeWidth,
          strokeDasharray: constants.shape.strokeDasharray,
          opacity: constants.shape.opacity,
          ...baseCamel,
        };
        const low = {
          fill: cam.fill,
          stroke: cam.stroke,
          strokewidth: cam.strokeWidth,
          strokedasharray: cam.strokeDasharray,
          opacity: cam.opacity,
          ...baseLower,
        };
        return { ...cam, ...low };
      }
      case AnnotationType.POLYLINE: {
        const cam = {
          fill: constants.shape.fill,
          stroke: constants.shape.stroke,
          strokeWidth: constants.shape.strokeWidth,
          strokeDasharray: constants.shape.strokeDasharray,
          opacity: constants.shape.opacity,
          ...baseCamel,
        };
        const low = {
          fill: cam.fill,
          stroke: cam.stroke,
          strokewidth: cam.strokeWidth,
          strokedasharray: cam.strokeDasharray,
          opacity: cam.opacity,
          ...baseLower,
        };
        return { ...cam, ...low };
      }
      case AnnotationType.POLYGON: {
        const cam = {
          fill: constants.shape.fill,
          stroke: constants.shape.stroke,
          strokeWidth: constants.shape.strokeWidth,
          strokeDasharray: constants.shape.strokeDasharray,
          opacity: constants.shape.opacity,
          ...baseCamel,
        };
        const low = {
          fill: cam.fill,
          stroke: cam.stroke,
          strokewidth: cam.strokeWidth,
          strokedasharray: cam.strokeDasharray,
          opacity: cam.opacity,
          ...baseLower,
        };
        return { ...cam, ...low };
      }
      case AnnotationType.ARROW: {
        const cam = {
          fill: constants.shape.fill,
          stroke: constants.shape.stroke,
          strokeWidth: constants.shape.strokeWidth,
          strokeDasharray: constants.shape.strokeDasharray,
          opacity: constants.shape.opacity,
          ...baseCamel,
        };
        const low = {
          fill: cam.fill,
          stroke: cam.stroke,
          strokewidth: cam.strokeWidth,
          strokedasharray: cam.strokeDasharray,
          opacity: cam.opacity,
          ...baseLower,
        };
        return { ...cam, ...low };
      }
      case AnnotationType.HIGHLIGHTER: {
        const cam = {
          fill: constants.highlighter.fill,
          stroke: constants.highlighter.stroke,
          strokeWidth: constants.highlighter.strokeWidth,
          strokeDasharray: constants.highlighter.strokeDasharray,
          opacity: constants.highlighter.opacity,
          ...baseCamel,
        };
        const low = {
          fill: cam.fill,
          stroke: cam.stroke,
          strokewidth: cam.strokeWidth,
          strokedasharray: cam.strokeDasharray,
          opacity: cam.opacity,
          ...baseLower,
        };
        return { ...cam, ...low };
      }
      case AnnotationType.HYPERLINK: {
        const cam = {
          fill: constants.shape.fill,
          stroke: constants.shape.stroke,
          strokeWidth: constants.shape.strokeWidth,
          strokeDasharray: constants.shape.strokeDasharray,
          opacity: constants.shape.opacity,
          ...baseCamel,
        };
        const low = {
          fill: cam.fill,
          stroke: cam.stroke,
          strokewidth: cam.strokeWidth,
          strokedasharray: cam.strokeDasharray,
          opacity: cam.opacity,
          ...baseLower,
        };
        return { ...cam, ...low };
      }
      default: {
        const cam = {
          fill: constants.shape.fill,
          stroke: constants.shape.stroke,
          strokeWidth: constants.shape.strokeWidth,
          strokeDasharray: constants.shape.strokeDasharray,
          opacity: constants.shape.opacity,
          ...baseCamel,
        };
        const low = {
          fill: cam.fill,
          stroke: cam.stroke,
          strokewidth: cam.strokeWidth,
          strokedasharray: cam.strokeDasharray,
          opacity: cam.opacity,
          ...baseLower,
        };
        return { ...cam, ...low };
      }
    }
  }, []);

  return getDefaultStyle;
};

/**
 * Map string type to proto enum (line tool as POLYLINE)
 * @param type - The string type of the annotation
 * @returns The proto enum of the annotation
 */
export const getProtoAnnotationType = (type: ProtoAnnotationType): AnnotationType => {
  switch (type) {
    case 'text':
      return AnnotationType.TEXT;
    case 'rectangle':
      return AnnotationType.RECTANGLE;
    case 'circle':
      return AnnotationType.CIRCLE;
    case 'ellipse':
      return AnnotationType.ELLIPSE;
    case 'line':
      return AnnotationType.LINE;
    case 'polyline':
      return AnnotationType.POLYLINE;
    case 'polygon':
      return AnnotationType.POLYGON;
    case 'path':
      return AnnotationType.PATH;
    case 'arrow':
      return AnnotationType.ARROW;
    case 'highlighter':
      return AnnotationType.HIGHLIGHTER;
    case 'freehand':
      return AnnotationType.FREEHAND;
    case 'hyperlink':
      return AnnotationType.HYPERLINK;
    default:
      return AnnotationType.TEXT;
  }
};

// Type for updateShape data that enforces all required properties
export interface UpdateShapeData {
  id: string;
  objectId: string;
  type: AnnotationType | undefined; // Allow undefined to match EnhancedAnnotation
  geometry: Partial<GeometryData.AsObject> | undefined; // Allow undefined to match EnhancedAnnotation
  style?: any;
  transform?: any;
  layerId?: string;
  zIndex?: number;
  isdimensional?: boolean; // Required to prevent flag loss
}

// Helper function to update shape (optimistic or real)
export const useUpdateShape = () => {
  const { optimisticShapes, updateOptimisticShape } = useAnnotationStore();
  const updateShapeMutation = useUpdateShapeAnnotationMutation();

  const updateShape = (shapeId: string, data: UpdateShapeData) => {
    const isShapeOptimistic = optimisticShapes.some((shape: any) => shape.id === shapeId);

    if (isShapeOptimistic) {
      // Update optimistic shape locally
      updateOptimisticShape(shapeId, {
        geometry: data.geometry,
        type: data.type,
        style: data.style,
        transform: data.transform, // Include transform in optimistic updates
        isdimensional: data.isdimensional, // Include dimensional flag in optimistic updates
      });
      // For optimistic shapes, clear live editing state immediately since update is synchronous
      setTimeout(() => {
        const store = (
          require('../annotations/annotationStore') as any
        ).useAnnotationStore?.getState?.();
        store?.setLiveEditingShape?.(null, null);
      }, 0);
    } else {
      // Update real shape via backend
      updateShapeMutation.mutate(data, {
        onSuccess: () => {
          // Clear live editing state after successful mutation
          const store = (
            require('../annotations/annotationStore') as any
          ).useAnnotationStore?.getState?.();
          store?.setLiveEditingShape?.(null, null, null);
        },
        onError: error => {
          // Clear live editing state on error too
          const store = (
            require('../annotations/annotationStore') as any
          ).useAnnotationStore?.getState?.();
          store?.setLiveEditingShape?.(null, null, null);
          console.error('❌ Mutation failed, live editing state cleared:', error);
        },
      });
    }
  };
  return updateShape;
};

// Helper function to get the center point of any shape type
export const getShapeCenter = (geometry: any, type: AnnotationType) => {
  switch (type) {
    case AnnotationType.CIRCLE:
    case AnnotationType.ELLIPSE:
      return {
        x: parseFloat(geometry?.cx || '0'),
        y: parseFloat(geometry?.cy || '0'),
      };
    case AnnotationType.LINE:
    case AnnotationType.ARROW:
      return {
        x: (parseFloat(geometry?.x1 || '0') + parseFloat(geometry?.x2 || '0')) / 2,
        y: (parseFloat(geometry?.y1 || '0') + parseFloat(geometry?.y2 || '0')) / 2,
      };
    case AnnotationType.POLYLINE:
    case AnnotationType.POLYGON: {
      if (geometry?.points) {
        const coords = geometry.points.split(' ').map((p: string) => {
          const [x, y] = p.split(',').map(Number);
          return { x, y };
        });
        if (coords.length > 0) {
          const sumX = coords.reduce((sum: number, p: { x: number; y: number }) => sum + p.x, 0);
          const sumY = coords.reduce((sum: number, p: { x: number; y: number }) => sum + p.y, 0);
          return {
            x: sumX / coords.length,
            y: sumY / coords.length,
          };
        }
      }
      return { x: 0, y: 0 };
    }
    default:
      // For rectangle-based shapes (TEXT, RECTANGLE, HIGHLIGHTER)
      return {
        x: parseFloat(geometry?.x || '0') + parseFloat(geometry?.width || '100') / 2,
        y: parseFloat(geometry?.y || '0') + parseFloat(geometry?.height || '50') / 2,
      };
  }
};

// Helper function to handle resize operations for rectangle-based shapes
export const handlePolygonMidpointResize = (
  handleId: string,
  start: { x: number; y: number },
  last: { x: number; y: number },
  original: EnhancedAnnotation
) => {
  const dx = last.x - start.x;
  const dy = last.y - start.y;

  if (!original.geometry?.points) return original.geometry;

  const coords = original.geometry.points.split(' ').map((p: string) => {
    const [x, y] = p.split(',').map(Number);
    return { x, y };
  });

  // Extract midpoint index from handleId (e.g., "midpoint-0" -> 0)
  const midpointIndex = parseInt(handleId.replace('midpoint-', ''), 10);

  if (midpointIndex < 0 || midpointIndex >= coords.length) return original.geometry;

  // Get the two vertices that define this edge
  const currentIndex = midpointIndex;
  const nextIndex = (midpointIndex + 1) % coords.length;

  const current = coords[currentIndex];
  const next = coords[nextIndex];

  // Calculate how much the midpoint moved
  const midDx = dx;
  const midDy = dy;

  // Move both vertices by half the midpoint movement to maintain the edge
  const newCurrent = {
    x: current.x + midDx / 2,
    y: current.y + midDy / 2,
  };
  const newNext = {
    x: next.x + midDx / 2,
    y: next.y + midDy / 2,
  };

  // Update the coordinates array
  const newCoords = [...coords];
  newCoords[currentIndex] = newCurrent;
  newCoords[nextIndex] = newNext;

  // Convert back to points string
  const newPoints = newCoords.map(p => `${p.x},${p.y}`).join(' ');

  return {
    ...original.geometry,
    points: newPoints,
  };
};

export const handleRectangleResize = (
  handleId: string,
  start: { x: number; y: number },
  last: { x: number; y: number },
  original: EnhancedAnnotation
) => {
  const dx = last.x - start.x;
  const dy = last.y - start.y;

  // Edge-anchored resizing (no center shift)
  const origX = parseFloat(original.geometry?.x || '0');
  const origY = parseFloat(original.geometry?.y || '0');
  const origW = parseFloat(original.geometry?.width || '0');
  const origH = parseFloat(original.geometry?.height || '0');

  let newX = origX;
  let newY = origY;
  let newW = origW;
  let newH = origH;

  // Horizontal
  if (handleId.includes('w')) {
    newX = origX + dx;
    newW = origW - dx;
  }
  if (handleId.includes('e')) {
    newW = (handleId.includes('w') ? newW : origW) + dx;
  }

  // Vertical
  if (handleId.includes('n')) {
    newY = origY + dy;
    newH = origH - dy;
  }
  if (handleId.includes('s')) {
    newH = (handleId.includes('n') ? newH : origH) + dy;
  }

  // Minimum dimensions
  const minWidth = original.type === AnnotationType.TEXT ? 50 : 1;
  const minHeight = original.type === AnnotationType.TEXT ? 30 : 1;

  if (newW < minWidth) {
    if (handleId.includes('w')) {
      // Anchor east side; pull west cannot cross east
      newX = origX + (origW - minWidth);
    }
    newW = minWidth;
  }
  if (newH < minHeight) {
    if (handleId.includes('n')) {
      // Anchor south side
      newY = origY + (origH - minHeight);
    }
    newH = minHeight;
  }

  return { x: String(newX), y: String(newY), width: String(newW), height: String(newH) };
};

// ===== NEW UTILITY FUNCTIONS EXTRACTED FROM SVGAnnotationRenderer =====

/**
 * Get shape-specific default colors and styles
 */
export const getShapeDefaults = (annotationType: AnnotationType) => {
  switch (annotationType) {
    case AnnotationType.RECTANGLE:
      return {
        fill: constants.shape.fill,
        stroke: constants.shape.stroke,
        strokeWidth: constants.shape.strokeWidth,
      };
    case AnnotationType.CIRCLE:
      return {
        fill: constants.shape.fill,
        stroke: constants.shape.stroke,
        strokeWidth: constants.shape.strokeWidth,
      };
    case AnnotationType.LINE:
    case AnnotationType.POLYLINE:
      return {
        fill: constants.shape.fill,
        stroke: constants.shape.stroke,
        strokeWidth: constants.shape.strokeWidth,
      };
    case AnnotationType.POLYGON:
      return {
        fill: constants.shape.fill,
        stroke: constants.shape.stroke,
        strokeWidth: constants.shape.strokeWidth,
      };
    case AnnotationType.ARROW:
      return {
        fill: 'none',
        stroke: constants.shape.stroke,
        strokeWidth: constants.shape.strokeWidth,
      };
    case AnnotationType.HIGHLIGHTER:
      return {
        fill: constants.highlighter.fill,
        stroke: constants.highlighter.stroke,
        strokeWidth: constants.highlighter.strokeWidth,
      };
    default:
      return {
        fill: constants.shape.fill,
        stroke: constants.shape.stroke,
        strokeWidth: constants.shape.strokeWidth,
      };
  }
};

/**
 * Get position from geometry or fallback to legacy fields
 */
export const getPosition = (geometry: any, fallbackX?: number, fallbackY?: number) => {
  const position =
    geometry?.x && geometry?.y
      ? {
          x: parseFloat(geometry.x),
          y: parseFloat(geometry.y),
        }
      : {
          x: fallbackX || 0,
          y: fallbackY || 0,
        };

  return position;
};

/**
 * Calculate rotation angle for any shape
 */
export const calculateRotationAngle = (
  start: { x: number; y: number },
  last: { x: number; y: number },
  original: EnhancedAnnotation,
  snapMode?: boolean,
  angleOptions?: number[]
) => {
  // Calculate center based on annotation type
  let center: { x: number; y: number };
  if (original.type === AnnotationType.CIRCLE || original.type === AnnotationType.ELLIPSE) {
    center = {
      x: parseFloat(original.geometry?.cx || '0'),
      y: parseFloat(original.geometry?.cy || '0'),
    };
  } else {
    center = {
      x: parseFloat(original.geometry?.x || '0') + parseFloat(original.geometry?.width || '0') / 2,
      y: parseFloat(original.geometry?.y || '0') + parseFloat(original.geometry?.height || '0') / 2,
    };
  }

  // Calculate rotation angle
  const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
  const currentAngle = Math.atan2(last.y - center.y, last.x - center.x);
  let rotation = ((currentAngle - startAngle) * 180) / Math.PI;

  // Add to existing rotation if any
  const existingRotation = parseFloat(original.transform?.rotation || '0');
  rotation += existingRotation;

  // Keep rotation between 0 and 360 degrees
  rotation = ((rotation % 360) + 360) % 360;

  // Apply angle snapping if enabled
  if (snapMode && angleOptions && angleOptions.length > 0) {
    // Find the closest snap angle
    const closestAngle = angleOptions.reduce((closest, angle) => {
      const diff1 = Math.abs(rotation - angle);
      const diff2 = Math.abs(rotation - (angle + 360));
      const diff3 = Math.abs(rotation - (angle - 360));

      const minDiff = Math.min(diff1, diff2, diff3);
      const currentClosestDiff = Math.abs(rotation - closest);

      return minDiff < currentClosestDiff ? angle : closest;
    });

    rotation = closestAngle;

    // Ensure we're still in 0-360 range
    rotation = ((rotation % 360) + 360) % 360;
  }

  return { rotation, center };
};

/**
 * Build transform string for SVG elements
 */
export const buildTransformString = (
  transform: any,
  geometry: any,
  annotationType: AnnotationType,
  isLiveEditing: boolean = false
) => {
  if (!transform) return '';

  const transforms: string[] = [];
  const { rotation, scalex, scaley, skewx, skewy } = transform;

  // For rotation, rotate around the current geometry center
  if (rotation) {
    // Use unified shape-center calculation for ALL shapes
    // - Circles/Ellipses: cx, cy
    // - Lines/Arrows: midpoint between endpoints
    // - Polylines/Polygons: centroid of points
    // - Rect/Text/Highlighter: box center (x+width/2, y+height/2)
    const { x: centerX, y: centerY } = getShapeCenter(geometry, annotationType);

    // Move to center, rotate, then move back
    transforms.push(`translate(${centerX}, ${centerY})`);
    transforms.push(`rotate(${rotation})`);
    transforms.push(`translate(${-centerX}, ${-centerY})`);
  }

  if (scalex || scaley) transforms.push(`scale(${scalex || 1}, ${scaley || 1})`);
  if (skewx) transforms.push(`skewX(${skewx})`);
  if (skewy) transforms.push(`skewY(${skewy})`);

  return transforms.join(' ');
};

/**
 * Constrain coordinates within bounds using simple approach
 */
export const constrainToBounds = (
  dx: number,
  dy: number,
  originalGeometry: any,
  annotationType: AnnotationType,
  bounds?: { width: number; height: number }
): any => {
  // If no bounds provided, use original behavior (no constraining)
  if (!bounds) {
    return moveShapeByDelta(dx, dy, originalGeometry, annotationType);
  }

  const { width: maxWidth, height: maxHeight } = bounds;

  switch (annotationType) {
    case AnnotationType.CIRCLE:
    case AnnotationType.ELLIPSE: {
      const cx = parseFloat(originalGeometry?.cx || '0');
      const cy = parseFloat(originalGeometry?.cy || '0');
      const r = parseFloat(originalGeometry?.r || '0');
      const rx = parseFloat(originalGeometry?.rx || '0');
      const ry = parseFloat(originalGeometry?.ry || '0');

      // Use radius for circles, or max of rx/ry for ellipses
      const radius = annotationType === AnnotationType.CIRCLE ? r : Math.max(rx, ry);

      const newCx = Math.max(radius, Math.min(maxWidth - radius, cx + dx));
      const newCy = Math.max(radius, Math.min(maxHeight - radius, cy + dy));

      return {
        ...originalGeometry,
        cx: String(newCx),
        cy: String(newCy),
      };
    }
    case AnnotationType.LINE:
    case AnnotationType.ARROW: {
      const x1 = parseFloat(originalGeometry?.x1 || '0');
      const y1 = parseFloat(originalGeometry?.y1 || '0');
      const x2 = parseFloat(originalGeometry?.x2 || '0');
      const y2 = parseFloat(originalGeometry?.y2 || '0');

      const newX1 = Math.max(0, Math.min(maxWidth, x1 + dx));
      const newY1 = Math.max(0, Math.min(maxHeight, y1 + dy));
      const newX2 = Math.max(0, Math.min(maxWidth, x2 + dx));
      const newY2 = Math.max(0, Math.min(maxHeight, y2 + dy));

      return {
        ...originalGeometry,
        x1: String(newX1),
        y1: String(newY1),
        x2: String(newX2),
        y2: String(newY2),
      };
    }
    case AnnotationType.POLYLINE:
    case AnnotationType.POLYGON: {
      if (originalGeometry?.points) {
        const coords = originalGeometry.points.split(' ').map((p: string) => {
          const [x, y] = p.split(',').map(Number);
          return {
            x: Math.max(0, Math.min(maxWidth, x + dx)),
            y: Math.max(0, Math.min(maxHeight, y + dy)),
          };
        });
        return {
          ...originalGeometry,
          points: coords.map((p: { x: number; y: number }) => `${p.x},${p.y}`).join(' '),
        };
      }
      return originalGeometry;
    }
    case AnnotationType.RECTANGLE:
    case AnnotationType.TEXT:
    case AnnotationType.HIGHLIGHTER: {
      const x = parseFloat(originalGeometry?.x || '0');
      const y = parseFloat(originalGeometry?.y || '0');
      const width = parseFloat(originalGeometry?.width || '0');
      const height = parseFloat(originalGeometry?.height || '0');

      const newX = Math.max(0, Math.min(maxWidth - width, x + dx));
      const newY = Math.max(0, Math.min(maxHeight - height, y + dy));

      return {
        ...originalGeometry,
        x: String(newX),
        y: String(newY),
      };
    }
    case AnnotationType.HYPERLINK:
    default: {
      const x = parseFloat(originalGeometry?.x || '0');
      const y = parseFloat(originalGeometry?.y || '0');
      const width = parseFloat(originalGeometry?.width || '24');
      const height = parseFloat(originalGeometry?.height || '24');

      const newX = Math.max(0, Math.min(maxWidth - width, x + dx));
      const newY = Math.max(0, Math.min(maxHeight - height, y + dy));

      return {
        ...originalGeometry, // This preserves hyperlinkdata and all other properties
        x: String(newX),
        y: String(newY),
      };
    }
  }
};

/**
 * Move shape coordinates directly by cursor delta (DEAD SIMPLE approach)
 */
export const moveShapeByDelta = (
  totalDx: number,
  totalDy: number,
  originalGeometry: any,
  annotationType: AnnotationType,
  bounds?: { width: number; height: number }
): any => {
  // If bounds are provided, use the constraining function
  if (bounds) {
    return constrainToBounds(totalDx, totalDy, originalGeometry, annotationType, bounds);
  }

  // Original behavior without bounds constraining
  switch (annotationType) {
    case AnnotationType.CIRCLE:
    case AnnotationType.ELLIPSE: {
      // Move center directly by cursor delta
      const cx = parseFloat(originalGeometry?.cx || '0');
      const cy = parseFloat(originalGeometry?.cy || '0');
      return {
        ...originalGeometry,
        cx: String(cx + totalDx),
        cy: String(cy + totalDy),
      };
    }
    case AnnotationType.LINE:
    case AnnotationType.ARROW: {
      // Move both endpoints directly by cursor delta
      const x1 = parseFloat(originalGeometry?.x1 || '0');
      const y1 = parseFloat(originalGeometry?.y1 || '0');
      const x2 = parseFloat(originalGeometry?.x2 || '0');
      const y2 = parseFloat(originalGeometry?.y2 || '0');
      return {
        ...originalGeometry,
        x1: String(x1 + totalDx),
        y1: String(y1 + totalDy),
        x2: String(x2 + totalDx),
        y2: String(y2 + totalDy),
      };
    }
    case AnnotationType.POLYLINE:
    case AnnotationType.POLYGON: {
      // Move each point directly by cursor delta
      if (originalGeometry?.points) {
        const coords = originalGeometry.points.split(' ').map((p: string) => {
          const [x, y] = p.split(',').map(Number);
          return { x: x + totalDx, y: y + totalDy };
        });
        return {
          ...originalGeometry,
          points: coords.map((p: { x: number; y: number }) => `${p.x},${p.y}`).join(' '),
        };
      } else {
        return originalGeometry;
      }
    }
    case AnnotationType.RECTANGLE:
    case AnnotationType.TEXT:
    case AnnotationType.HIGHLIGHTER: {
      // Move position directly by cursor delta
      const x = parseFloat(originalGeometry?.x || '0');
      const y = parseFloat(originalGeometry?.y || '0');
      return {
        ...originalGeometry,
        x: String(x + totalDx),
        y: String(y + totalDy),
      };
    }
    case AnnotationType.HYPERLINK:
    default:
      // For rectangles, text, highlighters, hyperlinks - move x,y directly by cursor delta
      const x = parseFloat(originalGeometry?.x || '0');
      const y = parseFloat(originalGeometry?.y || '0');
      return {
        ...originalGeometry, // This preserves hyperlinkdata and all other properties
        x: String(x + totalDx),
        y: String(y + totalDy),
      };
  }
};

/**
 * Round coordinates for final save
 */
export const roundGeometryCoordinates = (geometry: any) => {
  const rounded = { ...geometry };

  if (rounded.x) rounded.x = String(Math.round(parseFloat(rounded.x)));
  if (rounded.y) rounded.y = String(Math.round(parseFloat(rounded.y)));
  if (rounded.cx) rounded.cx = String(Math.round(parseFloat(rounded.cx)));
  if (rounded.cy) rounded.cy = String(Math.round(parseFloat(rounded.cy)));
  if (rounded.x1) rounded.x1 = String(Math.round(parseFloat(rounded.x1)));
  if (rounded.y1) rounded.y1 = String(Math.round(parseFloat(rounded.y1)));
  if (rounded.x2) rounded.x2 = String(Math.round(parseFloat(rounded.x2)));
  if (rounded.y2) rounded.y2 = String(Math.round(parseFloat(rounded.y2)));
  if (rounded.points) {
    const coords = rounded.points.split(' ').map((p: string) => {
      const [x, y] = p.split(',').map(Number);
      return `${Math.round(x)},${Math.round(y)}`;
    });
    rounded.points = coords.join(' ');
  }

  return rounded;
};

/**
 * Get effective geometry (live editing or fallback to annotation geometry)
 */
export const getEffectiveGeometry = (
  liveEditingGeometry: any,
  annotationGeometry: any,
  isLiveEditing: boolean
) => {
  return isLiveEditing && liveEditingGeometry ? liveEditingGeometry : annotationGeometry;
};

/**
 * Get effective transform (live editing or fallback to annotation transform)
 */
export const getEffectiveTransform = (
  liveEditingTransform: any,
  annotationTransform: any,
  isLiveEditing: boolean
) => {
  return isLiveEditing && liveEditingTransform ? liveEditingTransform : annotationTransform;
};

/**
 * Safely get annotation type name
 */
export const getAnnotationTypeName = (annotationType: AnnotationType | undefined | null) => {
  if (annotationType !== undefined && annotationType !== null) {
    const typeName = AnnotationType[annotationType];
    return typeName ? typeName.toLowerCase() : 'text';
  }
  return 'text';
};

// --- Multi-select helpers ---

/**
 * Check if an annotation would go out of bounds with the given movement
 */
export const wouldGoOutOfBounds = (
  geometry: any,
  type: AnnotationType,
  dx: number,
  dy: number,
  bounds: { width: number; height: number }
): boolean => {
  if (!bounds) return false;

  const { width: maxWidth, height: maxHeight } = bounds;

  switch (type) {
    case AnnotationType.CIRCLE:
    case AnnotationType.ELLIPSE: {
      const cx = parseFloat(geometry?.cx || '0') + dx;
      const cy = parseFloat(geometry?.cy || '0') + dy;
      const r = parseFloat(geometry?.r || '0');
      const rx = parseFloat(geometry?.rx || '0');
      const ry = parseFloat(geometry?.ry || '0');

      const radius = type === AnnotationType.CIRCLE ? r : Math.max(rx, ry);

      return (
        cx - radius < 0 || cx + radius > maxWidth || cy - radius < 0 || cy + radius > maxHeight
      );
    }
    case AnnotationType.LINE:
    case AnnotationType.ARROW: {
      const x1 = parseFloat(geometry?.x1 || '0') + dx;
      const y1 = parseFloat(geometry?.y1 || '0') + dy;
      const x2 = parseFloat(geometry?.x2 || '0') + dx;
      const y2 = parseFloat(geometry?.y2 || '0') + dy;

      return (
        x1 < 0 ||
        x1 > maxWidth ||
        x2 < 0 ||
        x2 > maxWidth ||
        y1 < 0 ||
        y1 > maxHeight ||
        y2 < 0 ||
        y2 > maxHeight
      );
    }
    case AnnotationType.POLYLINE:
    case AnnotationType.POLYGON: {
      if (geometry?.points) {
        const points = geometry.points.split(' ').map((p: string) => {
          const [x, y] = p.split(',').map(Number);
          return { x: x + dx, y: y + dy };
        });

        return points.some(
          (point: { x: number; y: number }) =>
            point.x < 0 || point.x > maxWidth || point.y < 0 || point.y > maxHeight
        );
      }
      return false;
    }
    case AnnotationType.RECTANGLE:
    case AnnotationType.TEXT:
    case AnnotationType.HIGHLIGHTER:
    case AnnotationType.HYPERLINK:
    default: {
      const x = parseFloat(geometry?.x || '0') + dx;
      const y = parseFloat(geometry?.y || '0') + dy;
      const width = parseFloat(geometry?.width || '24');
      const height = parseFloat(geometry?.height || '24');

      return x < 0 || x + width > maxWidth || y < 0 || y + height > maxHeight;
    }
  }
};

export const offsetGeometry = (
  geometry: any,
  dx: number,
  dy: number,
  type: AnnotationType,
  bounds?: { width: number; height: number }
) => {
  // If bounds are provided, use the constraining function
  if (bounds) {
    return constrainToBounds(dx, dy, geometry, type, bounds);
  }

  // Original behavior without bounds constraining
  const g = { ...(geometry || {}) } as any;
  switch (type) {
    case AnnotationType.RECTANGLE:
    case AnnotationType.TEXT:
    case AnnotationType.HIGHLIGHTER: {
      const x = parseFloat(g.x || '0');
      const y = parseFloat(g.y || '0');
      g.x = String(x + dx);
      g.y = String(y + dy);
      return g;
    }
    case AnnotationType.CIRCLE: {
      const cx = parseFloat(g.cx || '0');
      const cy = parseFloat(g.cy || '0');
      g.cx = String(cx + dx);
      g.cy = String(cy + dy);
      return g;
    }
    case AnnotationType.ELLIPSE: {
      const cx = parseFloat(g.cx || '0');
      const cy = parseFloat(g.cy || '0');
      g.cx = String(cx + dx);
      g.cy = String(cy + dy);
      return g;
    }
    case AnnotationType.LINE:
    case AnnotationType.ARROW: {
      const x1 = parseFloat(g.x1 || '0');
      const y1 = parseFloat(g.y1 || '0');
      const x2 = parseFloat(g.x2 || '0');
      const y2 = parseFloat(g.y2 || '0');
      g.x1 = String(x1 + dx);
      g.y1 = String(y1 + dy);
      g.x2 = String(x2 + dx);
      g.y2 = String(y2 + dy);
      return g;
    }
    case AnnotationType.POLYLINE:
    case AnnotationType.POLYGON: {
      const points: string = g.points || '';
      if (!points) return g;
      const shifted = points
        .split(' ')
        .filter(Boolean)
        .map(p => {
          const [px, py] = p.split(',').map(Number);
          return `${px + dx},${py + dy}`;
        })
        .join(' ');
      g.points = shifted;
      const x = parseFloat(g.x || '0');
      const y = parseFloat(g.y || '0');
      if (!isNaN(x) && !isNaN(y)) {
        g.x = String(x + dx);
        g.y = String(y + dy);
      }
      return g;
    }
    default:
      return g;
  }
};

/**
 * Apply snap angle constraints to a point based on a reference point
 * @param referencePoint - The fixed reference point (e.g., anchor or pivot)
 * @param targetPoint - The point to snap (e.g., pivot or end)
 * @param snapMode - Whether snap mode is enabled
 * @param angleOptions - Array of snap angles in degrees
 * @returns The snapped target point
 */
export const applySnapAngles = (
  referencePoint: { x: number; y: number },
  targetPoint: { x: number; y: number },
  snapMode: boolean,
  angleOptions: number[]
): { x: number; y: number } => {
  if (!snapMode || !angleOptions || angleOptions.length === 0) {
    return targetPoint;
  }

  // Calculate the angle from reference to target point
  const dx = targetPoint.x - referencePoint.x;
  const dy = targetPoint.y - referencePoint.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  if (distance === 0) {
    return targetPoint;
  }

  // Convert to degrees
  let angle = (Math.atan2(dy, dx) * 180) / Math.PI;

  // Normalize to 0-360 range
  angle = ((angle % 360) + 360) % 360;

  // Find the closest snap angle
  const closestAngle = angleOptions.reduce((closest, snapAngle) => {
    const diff1 = Math.abs(angle - snapAngle);
    const diff2 = Math.abs(angle - (snapAngle + 360));
    const diff3 = Math.abs(angle - (snapAngle - 360));

    const minDiff = Math.min(diff1, diff2, diff3);
    const currentClosestDiff = Math.abs(angle - closest);

    return minDiff < currentClosestDiff ? snapAngle : closest;
  });

  // Convert back to radians
  const snappedAngleRad = (closestAngle * Math.PI) / 180;

  // Calculate the snapped position
  const snappedX = referencePoint.x + distance * Math.cos(snappedAngleRad);
  const snappedY = referencePoint.y + distance * Math.sin(snappedAngleRad);

  return { x: snappedX, y: snappedY };
};

/**
 * Calculate arrow head geometry for consistent arrow rendering
 * @param x1 - Start X coordinate of the line
 * @param y1 - Start Y coordinate of the line
 * @param x2 - End X coordinate of the line (arrow tip position)
 * @param y2 - End Y coordinate of the line (arrow tip position)
 * @param arrowLength - Length of the arrow head (default: 15)
 * @param arrowAngle - Angle of the arrow head in radians (default: π/6 = 30°)
 * @returns Object containing line end coordinates and arrow head points
 */
export const calculateArrowHeadGeometry = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  arrowLength: number = 15,
  arrowAngle: number = Math.PI / 6
) => {
  // Calculate the angle of the line
  const lineAngle = Math.atan2(y2 - y1, x2 - x1);

  // Calculate where the line should end (at the base of the arrow head)
  // The base is where the arrow head lines intersect with the main line
  const arrowBaseLength = arrowLength * Math.cos(arrowAngle);
  const lineEndX = x2 - arrowBaseLength * Math.cos(lineAngle);
  const lineEndY = y2 - arrowBaseLength * Math.sin(lineAngle);

  // Calculate arrow head points so the tip sits exactly at (x2, y2)
  const arrowHead1X = x2 - arrowLength * Math.cos(lineAngle - arrowAngle);
  const arrowHead1Y = y2 - arrowLength * Math.sin(lineAngle - arrowAngle);
  const arrowHead2X = x2 - arrowLength * Math.cos(lineAngle + arrowAngle);
  const arrowHead2Y = y2 - arrowLength * Math.sin(lineAngle + arrowAngle);

  return {
    // Line end coordinates (where the line should stop)
    lineEndX,
    lineEndY,
    // Arrow head tip (where the arrow points)
    arrowTipX: x2,
    arrowTipY: y2,
    // Arrow head base points
    arrowHead1X,
    arrowHead1Y,
    arrowHead2X,
    arrowHead2Y,
    // Line angle for reference
    lineAngle,
  };
};

export const buildCreatePayloadFromAnnotation = (
  ann: any,
  targetObjectId: string,
  offsetIndex: number,
  deltaPerIndex: number = 12
) => {
  const type = ann.type as AnnotationType;
  const dx = offsetIndex * deltaPerIndex;
  const dy = offsetIndex * deltaPerIndex;
  const geometry = offsetGeometry(ann.geometry || {}, dx, dy, type);

  const id = `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  return {
    id,
    objectid: targetObjectId,
    annotationdata: {
      id,
      type,
      geometry,
      style: ann.style || {},
      transform: ann.transform,
      layerid: (ann.layerId as string) || '',
      locked: false,
      visible: true,
      groupid: '',
      selectable: true,
      draggable: true,
      resizable: true,
      custompropertiesMap: [],
      x: (ann.x as number | undefined)?.toString?.() || '0',
      y: (ann.y as number | undefined)?.toString?.() || '0',
      text: (ann.geometry?.text as string) || '',
      isdimensional: !!ann.isdimensional,
    } as any,
    createdbyuserid: ann.createdByUserId || '',
    createdbyaccountid: ann.createdByAccountId || '',
    lastupdated: '',
  };
};
